import { Button, Chip, CircularProgress } from '@mui/material';
import { filter } from 'lodash';
import React, { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CopyTextId,
  DataGrid,
  DataGridColumnDefinition,
  Dialog,
} from 'components';
import { TranslationNamespace } from 'i18n';
import {
  MANUAL_BATCH_TERMINAL_RESULTS,
  PayoutManualBatch,
  PayoutManualBatchItem,
  PayoutManualBatchStatus,
} from 'types';
import { orderUtils } from 'utils';

import { useRetryableManualOrders } from './use-retryable-manual-orders.hook';

type Props = {
  open: boolean;
  batch?: PayoutManualBatch;
  onClose: () => void;
  onRetryFailed: (orderIds: string[]) => void;
};

export const BatchResultDialog: React.FC<Props> = ({
  open,
  batch,
  onClose,
  onRetryFailed,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders.manual.result_dialog',
  });

  const {
    filteredRetryableIds,
    hasNoRetryableOrders,
    isChecking,
    getRetryableIds,
  } = useRetryableManualOrders(
    batch?.items,
    open && !!batch && batch.status !== PayoutManualBatchStatus.InProgress,
  );

  const columns: DataGridColumnDefinition<PayoutManualBatchItem>[] = useMemo(
    () => [
      {
        header: t('columns.order'),
        valueGetter: (item) => <CopyTextId id={item.orderId} />,
      },
      {
        header: t('columns.result'),
        valueGetter: (item) => (
          <Chip
            size="small"
            variant="outlined"
            color={orderUtils.getManualBatchResultColor(item.result)}
            label={orderUtils.getManualBatchResultLabel(
              item.result,
              batch?.operation,
            )}
          />
        ),
      },
      {
        header: t('columns.reason'),
        valueGetter: (item) => orderUtils.getManualBatchItemReason(item) || '—',
      },
    ],
    [t, batch?.operation],
  );

  const handleRetry = useCallback(async () => {
    const filteredAvailableIds = await getRetryableIds();
    if (filteredAvailableIds.length) {
      onRetryFailed(filteredAvailableIds);
    }
  }, [getRetryableIds, onRetryFailed]);

  const handleClose = useCallback(() => onClose(), [onClose]);

  const isInProgress = useMemo(
    () => !batch || batch.status === PayoutManualBatchStatus.InProgress,
    [batch],
  );

  // Items now exist from the moment the batch is claimed, so the wait shows real progress.
  const doneCount = useMemo(
    () =>
      filter(batch?.items, (item) =>
        MANUAL_BATCH_TERMINAL_RESULTS.includes(item.result),
      ).length,
    [batch?.items],
  );

  return (
    <Dialog
      open={open}
      title={t('title')}
      hideCancel
      maxWidth="md"
      okLabel={t('close')}
      onClose={handleClose}
    >
      {isInProgress && (
        <div className="tw-flex tw-items-center tw-gap-3">
          <CircularProgress size={20} />
          <div>
            <div>{t('in_progress', { count: batch?.totalCount ?? 0 })}</div>
            <div>
              {t('progress', {
                done: doneCount,
                total: batch?.totalCount ?? 0,
              })}
            </div>
          </div>
        </div>
      )}
      {!isInProgress && (
        <Fragment>
          {batch?.status === PayoutManualBatchStatus.Error && (
            <div className="tw-mb-4">{t('interrupted')}</div>
          )}
          <div className="tw-flex tw-flex-wrap tw-gap-2">
            <Chip
              variant="outlined"
              label={t('total', { count: batch?.totalCount ?? 0 })}
            />
            <Chip
              variant="outlined"
              color="success"
              label={t('succeeded', { count: batch?.successCount ?? 0 })}
            />
            {!!batch?.failedCount && (
              <Chip
                variant="outlined"
                color="error"
                label={t('failed', { count: batch.failedCount })}
              />
            )}
            {!!batch?.disputeCount && (
              <Chip
                variant="outlined"
                color="warning"
                label={t('disputed', { count: batch.disputeCount })}
              />
            )}
            {!!batch?.skippedCount && (
              <Chip
                variant="outlined"
                label={t('skipped', { count: batch.skippedCount })}
              />
            )}
          </div>
          <div className="tw-mt-4 tw-mb-2">{t('orders')}</div>
          <DataGrid columns={columns} data={batch?.items || []} />
          {hasNoRetryableOrders && <div>{t('no_retryable_orders')}</div>}
          {!!filteredRetryableIds.length && (
            <div className="tw-mt-4">
              <Button
                variant="outlined"
                disabled={isChecking}
                onClick={handleRetry}
              >
                {t('retry_failed', { count: filteredRetryableIds.length })}
              </Button>
            </div>
          )}
        </Fragment>
      )}
    </Dialog>
  );
};
